import React, { useState } from "react";
import { api, vendorCategories, money } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, Mail, Phone, Copy, Settings, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import VendorDetailDialog from "../VendorDetailDialog";
import SuggestionChips from "../SuggestionChips";

const ROLES = [
  { value: "coordinator", label: "Coordinator" },
  { value: "vendor", label: "Vendor" },
  { value: "client", label: "Client / Couple" },
];

export default function VendorsTab({ eventId, members, canEdit, reload, onPreviewAs }) {
  const [open, setOpen] = useState(false);
  const [detailMember, setDetailMember] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "vendor", vendor_category: "Catering", phone: "", notes: "" });

  const quickAddVendor = async (category) => {
    // Opens the Add Person dialog prefilled with the vendor category.
    setForm({ name: "", email: "", role: "vendor", vendor_category: category, phone: "", notes: "" });
    setOpen(true);
  };

  const add = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    try {
      await api.post(`/events/${eventId}/members`, form);
      setOpen(false);
      setForm({ name: "", email: "", role: "vendor", vendor_category: "Catering", phone: "", notes: "" });
      toast.success("Added to team");
      reload();
    } catch (e) { toast.error("Could not add"); }
  };

  const del = async (id) => {
    if (!confirm("Remove this person from the event?")) return;
    try { await api.delete(`/events/${eventId}/members/${id}`); reload(); }
    catch (e) { toast.error("Could not remove"); }
  };

  const copyInvite = (m) => {
    const link = `${window.location.origin}/`;
    navigator.clipboard.writeText(`You're invited to help plan an event. Sign in with Google (${m.email}) at ${link} to see your tasks & timeline.`);
    toast.success("Invite message copied");
  };

  const sendInvite = async (m) => {
    if (!m.email) { toast.error("This person has no email on file"); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/members/${m.id}/invite`);
      toast.success(`Portal invite sent to ${data.to}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invite");
    }
  };

  const byRole = (r) => members.filter(m => m.role === r);
  const groups = [
    { title: "Coordinators", role: "coordinator" },
    { title: "Vendors", role: "vendor" },
    { title: "Clients", role: "client" },
  ];

  return (
    <div className="space-y-10">
      {canEdit && (
        <SuggestionChips
          eventId={eventId}
          kind="vendor"
          onAdd={quickAddVendor}
          version={members.length}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.VENDORS / TEAM</div>
          <h2 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">
            <span className="font-mono tabular-nums">{members.length.toString().padStart(2, "0")}</span> people
          </h2>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-member-btn" className="rounded-sm bg-app hover:bg-[#000000]">
                <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add person
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-surface rounded-sm max-w-lg">
              <DialogHeader><DialogTitle className="font-serif text-2xl text-app">Add to event</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <F label="Role">
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="member-role-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                {form.role === "vendor" && (
                  <F label="Vendor category">
                    <Select value={form.vendor_category} onValueChange={(v) => setForm({ ...form, vendor_category: v })}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{vendorCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                )}
                <F label="Name"><Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
                <F label="Email (their Google account)"><Input data-testid="member-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="they@example.com" /></F>
                <F label="Phone (optional)"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
                <F label="Notes"><Textarea rows={2} className="rounded-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="member-submit" onClick={add} className="rounded-sm bg-app hover:bg-[#000000]">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {groups.map(g => {
        const list = byRole(g.role);
        if (list.length === 0) return null;
        return (
          <section key={g.role}>
            <div className="border-b border-hair pb-3 mb-5 flex items-baseline gap-3">
              <h3 className="font-serif text-2xl text-app">{g.title}</h3>
              <span className="eyebrow">{list.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map(m => (
                <div
                  key={m.id}
                  data-testid={`member-card-${m.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetailMember(m)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setDetailMember(m)}
                  className="bevel border-hair rounded-sm p-5 card-lift cursor-pointer hover:border-[#0A0A0A]/40 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/20"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {m.vendor_category && <div className="eyebrow mb-1">{m.vendor_category}</div>}
                      <div className="font-serif text-xl text-app truncate">{m.name}</div>
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {onPreviewAs && (
                        <button
                          onClick={() => onPreviewAs(m)}
                          data-testid={`preview-as-${m.id}`}
                          title={`View this event as ${m.name} would see it`}
                          className="p-1 text-muted hover:text-[#F59E0B]"
                        >
                          <Eye size={14} strokeWidth={1.5} />
                        </button>
                      )}
                      <button
                        onClick={() => sendInvite(m)}
                        data-testid={`invite-member-${m.id}`}
                        title={m.invited_at ? "Resend portal invite" : "Send portal invite"}
                        className={`p-1 ${m.invited_at ? "text-[#059669]" : "text-muted"} hover:text-app`}
                      >
                        <Send size={14} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => copyInvite(m)} title="Copy invite message" className="text-muted hover:text-app p-1">
                        <Copy size={14} strokeWidth={1.5} />
                      </button>
                      {canEdit && (
                        <button onClick={() => del(m.id)} data-testid={`delete-member-${m.id}`} className="text-muted hover:text-[#EF4444] p-1">
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5 text-sm font-light text-muted">
                    <div className="flex items-center gap-2">
                      <Mail size={13} strokeWidth={1.5} />
                      <a href={`mailto:${m.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-app truncate">{m.email}</a>
                    </div>
                    {m.phone && (
                      <div className="flex items-center gap-2"><Phone size={13} strokeWidth={1.5} /><span>{m.phone}</span></div>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-hair flex items-center gap-2 flex-wrap">
                    <InviteStatusPill member={m} />
                    {m.invited_at && (
                      <span className="eyebrow px-2 py-0.5 rounded-sm bg-surface-2 text-muted" title={`Last invite sent ${new Date(m.invited_at).toLocaleString()}`}>
                        Sent {relativeTime(m.invited_at)}
                      </span>
                    )}
                    {m.email && (m.invite_status || "pending") === "pending" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); sendInvite(m); }}
                        data-testid={`resend-invite-${m.id}`}
                        className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border border-hair hover:border-[#0A0A0A] text-app"
                      >
                        <Send size={11} strokeWidth={2} />
                        {m.invited_at ? "Resend invite" : "Send invite"}
                      </button>
                    )}
                  </div>
                  {m.notes && <p className="mt-3 text-sm text-muted font-light">{m.notes}</p>}
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {members.length === 0 && (
        <div className="bevel border-hair rounded-sm p-10 text-center">
          <div className="font-serif text-2xl text-app mb-2">No one on the team yet.</div>
          <p className="text-muted font-light">Add vendors, coordinators, and clients so everyone works from the same plan.</p>
        </div>
      )}

      <VendorDetailDialog
        eventId={eventId}
        member={detailMember}
        open={!!detailMember}
        onOpenChange={(v) => !v && setDetailMember(null)}
        canEdit={canEdit}
        onSaved={reload}
        onPreviewAs={onPreviewAs}
      />
    </div>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}

// ============ Invitation status helpers ============
function InviteStatusPill({ member }) {
  // Infer a status when the field is missing (legacy records surface as
  // "signed in" or "not sent" based on user_id + invited_at).
  const status = member.invite_status
    || (member.user_id ? "accepted" : member.invited_at ? "pending" : "not_sent");
  const meta = {
    accepted: { label: "Accepted", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30" },
    pending:  { label: "Pending",  cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30" },
    declined: { label: "Declined", cls: "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30" },
    not_sent: { label: "Not sent", cls: "bg-canvas text-muted border border-hair" },
  }[status] || { label: status, cls: "bg-surface-2 text-muted" };
  return (
    <span
      className={`eyebrow px-2 py-0.5 rounded-sm ${meta.cls}`}
      data-testid={`invite-status-${status}`}
    >
      {meta.label}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diffMs = Date.now() - then;
    if (diffMs < 0) return "just now";
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

